import {
  ARENA_BOUNDS,
  BALL_RADIUS,
  BASE_BALL_COUNT,
  DEFAULT_LEVEL_PARAMETERS,
} from "./config";
import type { AuthoredLevelParameters, WallColor } from "./config";

export type LevelId = string;
export type LevelReference = LevelId | number;
type Charge = -1 | 1;

export interface LevelBallDefinition {
  x: number;
  y: number;
  vx: number;
  vy: number;
  charge: Charge;
}

export interface LevelDefinition {
  level: LevelId;
  durationMs: number;
  forceStrength: number;
  bounce: number;
  walls: {
    left: WallColor;
    right: WallColor;
    top: WallColor;
    bottom: WallColor;
  };
  balls: LevelBallDefinition[];
}

interface OrbitBallTuning {
  charge: Charge;
  angleDeg: number;
  speedScale?: number;
  radialBias?: number;
}

interface AuthoredLevelTuning extends Partial<AuthoredLevelParameters> {
  balls: OrbitBallTuning[];
}

interface ProgressionLevelSpec extends Partial<AuthoredLevelParameters> {
  ballCount: number;
  startCharge?: Charge;
  angleOffsetDeg?: number;
  speedScalePattern?: readonly number[];
  radialBiasPattern?: readonly number[];
  angleJitterPattern?: readonly number[];
}

type AuthoredLevelKey = `level_${string}`;
type AuthoredLevelSpecMap = Record<AuthoredLevelKey, ProgressionLevelSpec>;

class DeterministicRandom {
  private state: number;

  constructor(seed: number) {
    this.state = seed >>> 0 || 1;
  }

  next(): number {
    this.state = (Math.imul(this.state, 1664525) + 1013904223) >>> 0;
    return this.state / 0x1_0000_0000;
  }
}

const LEVEL_ID_PATTERN = /^\d+(?:\.\d+)*$/;
const EARLY_LEVEL_SPEED_MULTIPLIER = 1.5;

const AUTHORED_LEVEL_SPECS = {
  level_1: {
    ballCount: 3,
    bounce: 1,
    durationMs: 10_000,
    radiusX: 310,
    radiusY: 252,
    baseSpeed: 40,
    rotationDeg: 0,
    speedScalePattern: [0.78, 0.84, 0.8],
    radialBiasPattern: [4, 0, -4],
    angleJitterPattern: [0, -4, 4],
    forceStrength: 50_000_000,
    leftWallColor: "gray",
    rightWallColor: "gray",
    topWallColor: "red",
    bottomWallColor: "blue",
  },
  level_1_1: {
    ballCount: 3,
    bounce: 1,
    durationMs: 10_000,
    radiusX: 310,
    radiusY: 252,
    baseSpeed: 40,
    rotationDeg: 0,
    speedScalePattern: [0.78, 0.84, 0.8],
    radialBiasPattern: [4, 0, -4],
    angleJitterPattern: [0, -4, 4],
    forceStrength: 100_000_000,
    leftWallColor: "red",
    rightWallColor: "red",
    topWallColor: "blue",
    bottomWallColor: "blue",
  },
  level_2: {
    ballCount: 4,
    bounce: 0.94,
    durationMs: 10_000,
    radiusX: 322,
    radiusY: 260,
    baseSpeed: 12.5 * EARLY_LEVEL_SPEED_MULTIPLIER,
    rotationDeg: 4,
    speedScalePattern: [0.72, 0.78, 0.74, 0.8],
    radialBiasPattern: [3, -1, 1, -2],
    angleJitterPattern: [0, -4, 4, 0],
    forceStrength: 100_000_000,
    leftWallColor: "gray",
    rightWallColor: "gray",
    topWallColor: "red",
    bottomWallColor: "blue",
  },
  level_2_1: {
    ballCount: 4,
    bounce: 0.94,
    durationMs: 10_000,
    radiusX: 322,
    radiusY: 260,
    baseSpeed: 12.5 * EARLY_LEVEL_SPEED_MULTIPLIER,
    rotationDeg: 4,
    speedScalePattern: [0.72, 0.78, 0.74, 0.8],
    radialBiasPattern: [3, -1, 1, -2],
    angleJitterPattern: [0, -4, 4, 0],
    forceStrength: 70_000_000,
    leftWallColor: "red",
    rightWallColor: "blue",
    topWallColor: "gray",
    bottomWallColor: "gray",
  },
  level_2_2: {
    ballCount: 4,
    bounce: 0.94,
    durationMs: 15_000,
    radiusX: 322,
    radiusY: 260,
    baseSpeed: 12.5 * EARLY_LEVEL_SPEED_MULTIPLIER,
    rotationDeg: 4,
    speedScalePattern: [0.72, 0.78, 0.74, 0.8],
    radialBiasPattern: [3, -1, 1, -2],
    angleJitterPattern: [0, -4, 4, 0],
    forceStrength: 80_000_000,
    leftWallColor: "red",
    rightWallColor: "red",
    topWallColor: "blue",
    bottomWallColor: "blue",
  },
  level_3: {
    ballCount: 5,
    bounce: 0.94,
    durationMs: 15_000,
    radiusX: 276,
    radiusY: 216,
    baseSpeed: 27 * EARLY_LEVEL_SPEED_MULTIPLIER,
    rotationDeg: 0,
    speedScalePattern: [0.92, 0.99, 0.95, 1.02, 0.97],
    radialBiasPattern: [2, -2, 0, 2, -2],
    angleJitterPattern: [0, -4, 5, -3, 2],
    leftWallColor: "gray",
    rightWallColor: "gray",
    topWallColor: "red",
    bottomWallColor: "blue",
  },
  level_4: {
    ballCount: 5,
    bounce: 0.94,
    durationMs: 10_000,
    radiusX: 272,
    radiusY: 212,
    baseSpeed: 28.5 * EARLY_LEVEL_SPEED_MULTIPLIER,
    rotationDeg: 8,
    speedScalePattern: [0.94, 1.01, 0.97, 1.04, 0.99],
    radialBiasPattern: [2, -3, 0, 3, -2],
    angleJitterPattern: [0, -5, 6, -4, 3],
    leftWallColor: "blue",
    rightWallColor: "gray",
    topWallColor: "red",
    bottomWallColor: "gray",
  },
  level_5: {
    ballCount: 5,
    bounce: 0.94,
    durationMs: 10_000,
    radiusX: 268,
    radiusY: 208,
    baseSpeed: 30 * EARLY_LEVEL_SPEED_MULTIPLIER,
    rotationDeg: -10,
    swirl: "ccw",
    speedScalePattern: [0.96, 1.03, 0.99, 1.06, 1],
    radialBiasPattern: [1, -3, 1, 3, -2],
    angleJitterPattern: [0, -5, 5, -3, 3],
    leftWallColor: "gray",
    rightWallColor: "red",
    topWallColor: "gray",
    bottomWallColor: "blue",
  },
  level_6: {
    ballCount: 5,
    bounce: 0.94,
    durationMs: 10_000,
    radiusX: 264,
    radiusY: 204,
    baseSpeed: 31.5 * EARLY_LEVEL_SPEED_MULTIPLIER,
    rotationDeg: 12,
    speedScalePattern: [0.98, 1.05, 1, 1.07, 1.02],
    radialBiasPattern: [1, -4, 1, 4, -2],
    angleJitterPattern: [0, -6, 6, -4, 4],
    leftWallColor: "red",
    rightWallColor: "blue",
    topWallColor: "gray",
    bottomWallColor: "gray",
  },
  level_7: {
    ballCount: 6,
    bounce: 0.94,
    durationMs: 10_000,
    radiusX: 266,
    radiusY: 206,
    baseSpeed: 30.5 * EARLY_LEVEL_SPEED_MULTIPLIER,
    rotationDeg: -4,
    speedScalePattern: [0.96, 1.02, 0.98, 1.04, 0.99, 1.01],
    radialBiasPattern: [1, -2, 2, -2, 2, -1],
    angleJitterPattern: [0, -3, 4, -2, 3, -1],
    leftWallColor: "gray",
    rightWallColor: "gray",
    topWallColor: "red",
    bottomWallColor: "blue",
  },
  level_8: {
    ballCount: 6,
    bounce: 0.94,
    durationMs: 10_000,
    radiusX: 262,
    radiusY: 202,
    baseSpeed: 31.5 * EARLY_LEVEL_SPEED_MULTIPLIER,
    rotationDeg: 8,
    swirl: "ccw",
    speedScalePattern: [0.97, 1.03, 0.99, 1.05, 1, 1.02],
    radialBiasPattern: [1, -3, 3, -3, 3, -1],
    angleJitterPattern: [0, -4, 5, -3, 4, -2],
    leftWallColor: "blue",
    rightWallColor: "gray",
    topWallColor: "red",
    bottomWallColor: "gray",
  },
  level_9: {
    ballCount: 6,
    bounce: 0.94,
    durationMs: 10_000,
    radiusX: 258,
    radiusY: 198,
    baseSpeed: 33 * EARLY_LEVEL_SPEED_MULTIPLIER,
    rotationDeg: -12,
    speedScalePattern: [0.98, 1.05, 1, 1.07, 1.01, 1.03],
    radialBiasPattern: [0, -3, 3, -3, 3, 0],
    angleJitterPattern: [0, -4, 5, -4, 4, -1],
    leftWallColor: "red",
    rightWallColor: "blue",
    topWallColor: "gray",
    bottomWallColor: "gray",
  },
  level_10: {
    ballCount: 6,
    bounce: 0.94,
    durationMs: 10_000,
    radiusX: 254,
    radiusY: 194,
    baseSpeed: 34.5 * EARLY_LEVEL_SPEED_MULTIPLIER,
    rotationDeg: 16,
    speedScalePattern: [1, 1.06, 1.01, 1.08, 1.02, 1.04],
    radialBiasPattern: [0, -4, 4, -4, 4, 0],
    angleJitterPattern: [0, -5, 6, -4, 5, -2],
    leftWallColor: "blue",
    rightWallColor: "blue",
    topWallColor: "red",
    bottomWallColor: "red",
  },
  level_11: {
    ballCount: 7,
    bounce: 0.94,
    radiusX: 256,
    radiusY: 196,
    baseSpeed: 33,
    rotationDeg: -6,
    speedScalePattern: [0.96, 1.02, 0.98, 1.04, 1, 1.03, 0.99],
    radialBiasPattern: [1, -2, 2, -2, 2, -2, 1],
    angleJitterPattern: [0, -3, 4, -2, 3, -1, 2],
    leftWallColor: "gray",
    rightWallColor: "gray",
    topWallColor: "red",
    bottomWallColor: "blue",
  },
  level_12: {
    ballCount: 7,
    bounce: 0.94,
    radiusX: 250,
    radiusY: 190,
    baseSpeed: 33.5,
    rotationDeg: 10,
    swirl: "ccw",
    speedScalePattern: [0.96, 1.01, 0.98, 1.03, 0.99, 1.02, 0.98],
    radialBiasPattern: [1, -2, 3, -2, 3, -2, 1],
    angleJitterPattern: [0, -3, 4, -2, 3, -1, 2],
    leftWallColor: "blue",
    rightWallColor: "gray",
    topWallColor: "red",
    bottomWallColor: "gray",
  },
  level_13: {
    ballCount: 7,
    bounce: 0.94,
    radiusX: 262,
    radiusY: 202,
    baseSpeed: 33.8,
    rotationDeg: -14,
    speedScalePattern: [0.96, 1.01, 0.98, 1.03, 0.99, 1.02, 0.98],
    radialBiasPattern: [0, -2, 3, -3, 3, -2, 0],
    angleJitterPattern: [0, -3, 4, -3, 3, -1, 2],
    leftWallColor: "red",
    rightWallColor: "blue",
    topWallColor: "gray",
    bottomWallColor: "gray",
  },
  level_14: {
    ballCount: 7,
    bounce: 0.94,
    radiusX: 270,
    radiusY: 208,
    baseSpeed: 34.2,
    rotationDeg: 18,
    speedScalePattern: [0.97, 1.02, 0.99, 1.04, 1, 1.03, 0.99],
    radialBiasPattern: [0, -2, 3, -3, 3, -2, 0],
    angleJitterPattern: [0, -3, 4, -3, 3, -2, 2],
    leftWallColor: "blue",
    rightWallColor: "blue",
    topWallColor: "red",
    bottomWallColor: "red",
  },
  level_15: {
    ballCount: 8,
    bounce: 0.94,
    radiusX: 256,
    radiusY: 196,
    baseSpeed: 33.8,
    rotationDeg: -8,
    speedScalePattern: [0.96, 1, 0.97, 1.02, 0.98, 1.01, 0.98, 0.99],
    radialBiasPattern: [1, -1, 2, -2, 2, -2, 2, -1],
    angleJitterPattern: [0, -2, 3, -2, 3, -1, 2, -1],
    leftWallColor: "gray",
    rightWallColor: "gray",
    topWallColor: "red",
    bottomWallColor: "blue",
  },
  level_16: {
    ballCount: 8,
    bounce: 0.94,
    radiusX: 266,
    radiusY: 204,
    baseSpeed: 34.4,
    rotationDeg: 12,
    swirl: "ccw",
    speedScalePattern: [0.97, 1.01, 0.98, 1.03, 0.99, 1.02, 0.99, 1],
    radialBiasPattern: [0, -2, 2, -2, 2, -2, 2, 0],
    angleJitterPattern: [0, -2, 3, -2, 3, -1, 2, -1],
    leftWallColor: "blue",
    rightWallColor: "red",
    topWallColor: "gray",
    bottomWallColor: "gray",
  },
  level_17: {
    ballCount: 8,
    bounce: 0.94,
    radiusX: 258,
    radiusY: 198,
    baseSpeed: 35.2,
    rotationDeg: -16,
    speedScalePattern: [0.98, 1.02, 0.99, 1.04, 1, 1.03, 1, 1.01],
    radialBiasPattern: [0, -2, 3, -3, 3, -3, 3, 0],
    angleJitterPattern: [0, -3, 4, -3, 4, -2, 3, -1],
    leftWallColor: "red",
    rightWallColor: "red",
    topWallColor: "blue",
    bottomWallColor: "blue",
  },
  level_18: {
    ballCount: 9,
    bounce: 0.94,
    radiusX: 266,
    radiusY: 206,
    baseSpeed: 34.8,
    rotationDeg: 10,
    speedScalePattern: [0.96, 1, 0.98, 1.02, 0.99, 1.01, 0.99, 1, 0.97],
    radialBiasPattern: [0, -2, 2, -2, 2, -2, 2, -2, 0],
    angleJitterPattern: [0, -2, 3, -2, 3, -1, 2, -1, 1],
    leftWallColor: "red",
    rightWallColor: "blue",
    topWallColor: "gray",
    bottomWallColor: "gray",
  },
  level_19: {
    ballCount: 9,
    bounce: 0.94,
    radiusX: 272,
    radiusY: 212,
    baseSpeed: 35.4,
    rotationDeg: -18,
    swirl: "ccw",
    speedScalePattern: [0.97, 1.01, 0.99, 1.03, 1, 1.02, 1, 1.01, 0.98],
    radialBiasPattern: [0, -2, 2, -2, 2, -2, 2, -2, 0],
    angleJitterPattern: [0, -2, 3, -2, 3, -1, 2, -1, 1],
    leftWallColor: "blue",
    rightWallColor: "blue",
    topWallColor: "red",
    bottomWallColor: "red",
  },
  level_20: {
    ballCount: 10,
    bounce: 0.94,
    radiusX: 252,
    radiusY: 192,
    baseSpeed: 35.2,
    rotationDeg: 12,
    speedScalePattern: [0.96, 1, 0.98, 1.02, 0.99, 1.01, 0.99, 1.01, 0.98, 1],
    radialBiasPattern: [0, -2, 2, -2, 2, -2, 2, -2, 2, 0],
    angleJitterPattern: [0, -2, 3, -2, 3, -1, 2, -1, 2, -2],
    leftWallColor: "blue",
    rightWallColor: "red",
    topWallColor: "gray",
    bottomWallColor: "gray",
  },
  level_21: {
    ballCount: 10,
    bounce: 0.94,
    radiusX: 258,
    radiusY: 198,
    baseSpeed: 35.8,
    rotationDeg: -20,
    swirl: "ccw",
    speedScalePattern: [0.97, 1.01, 0.99, 1.03, 1, 1.02, 1, 1.02, 0.99, 1.01],
    radialBiasPattern: [0, -2, 2, -2, 2, -2, 2, -2, 2, 0],
    angleJitterPattern: [0, -2, 3, -2, 3, -1, 2, -1, 2, -1],
    leftWallColor: "red",
    rightWallColor: "red",
    topWallColor: "blue",
    bottomWallColor: "blue",
  },
} as const satisfies AuthoredLevelSpecMap;

const AUTHORED_LEVELS = buildAuthoredLevels(AUTHORED_LEVEL_SPECS);
const AUTHORED_LEVEL_IDS = Object.freeze(
  (Object.keys(AUTHORED_LEVEL_SPECS) as AuthoredLevelKey[])
    .map(levelKeyToId)
    .sort(compareLevelIds),
);
const LAST_AUTHORED_LEVEL_ID =
  AUTHORED_LEVEL_IDS[AUTHORED_LEVEL_IDS.length - 1] ?? "1";
const FIRST_GENERATED_LEVEL_MAJOR = getPrimaryLevelNumber(LAST_AUTHORED_LEVEL_ID) + 1;
const FIRST_GENERATED_LEVEL_ID = String(FIRST_GENERATED_LEVEL_MAJOR);
const FINAL_AUTHORED_BALL_COUNT =
  AUTHORED_LEVEL_SPECS[levelIdToKey(LAST_AUTHORED_LEVEL_ID)]?.ballCount ??
  BASE_BALL_COUNT;

export function getAuthoredLevelIds(): LevelId[] {
  return [...AUTHORED_LEVEL_IDS];
}

export function normalizeLevelId(level: LevelReference): LevelId | null {
  const rawLevelId =
    typeof level === "number"
      ? Number.isFinite(level) && level >= 1
        ? String(level)
        : null
      : level.trim();
  if (!rawLevelId || !LEVEL_ID_PATTERN.test(rawLevelId)) {
    return null;
  }

  const normalizedSegments = rawLevelId.split(".").map((segment) => {
    const numericSegment = Number.parseInt(segment, 10);
    return Number.isNaN(numericSegment) ? null : String(numericSegment);
  });
  if (normalizedSegments.some((segment) => segment === null)) {
    return null;
  }

  const [majorSegment] = normalizedSegments;
  if (majorSegment === "0") {
    return null;
  }

  return normalizedSegments.join(".");
}

export function canLoadLevelId(level: LevelReference): boolean {
  const levelId = normalizeLevelId(level);
  if (!levelId) {
    return false;
  }

  return AUTHORED_LEVELS[levelId] !== undefined || isGeneratedLevelId(levelId);
}

export function getNextLevelId(level: LevelReference): LevelId | null {
  const levelId = normalizeLevelId(level);
  if (!levelId) {
    return null;
  }

  const authoredIndex = AUTHORED_LEVEL_IDS.indexOf(levelId);
  if (authoredIndex !== -1) {
    return AUTHORED_LEVEL_IDS[authoredIndex + 1] ?? FIRST_GENERATED_LEVEL_ID;
  }

  if (isGeneratedLevelId(levelId)) {
    return String(getPrimaryLevelNumber(levelId) + 1);
  }

  return null;
}

export function getPreviousLevelId(level: LevelReference): LevelId | null {
  const levelId = normalizeLevelId(level);
  if (!levelId) {
    return null;
  }

  const authoredIndex = AUTHORED_LEVEL_IDS.indexOf(levelId);
  if (authoredIndex !== -1) {
    return AUTHORED_LEVEL_IDS[authoredIndex - 1] ?? null;
  }

  if (isGeneratedLevelId(levelId)) {
    const primaryLevelNumber = getPrimaryLevelNumber(levelId);
    if (primaryLevelNumber === FIRST_GENERATED_LEVEL_MAJOR) {
      return LAST_AUTHORED_LEVEL_ID;
    }
    return String(primaryLevelNumber - 1);
  }

  return null;
}

export function getLevelDefinition(level: LevelReference): LevelDefinition {
  const levelId = normalizeLevelId(level);
  if (!levelId) {
    throw new Error(`Invalid level id: ${String(level)}`);
  }

  const tuning = AUTHORED_LEVELS[levelId];
  if (tuning) {
    return createAuthoredLevel(levelId, tuning);
  }

  if (isGeneratedLevelId(levelId)) {
    return createGeneratedLevel(levelId);
  }

  throw new Error(`Unknown authored level id: ${levelId}`);
}

export function seedForLevel(level: LevelReference): number {
  const levelId = normalizeLevelId(level);
  if (!levelId) {
    throw new Error(`Invalid level id: ${String(level)}`);
  }

  let hash = 2166136261;
  for (const character of levelId) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0 || 1;
}

function createAuthoredLevel(
  level: LevelId,
  tuning: AuthoredLevelTuning,
): LevelDefinition {
  const centerX = ARENA_BOUNDS.x + ARENA_BOUNDS.width / 2;
  const centerY = ARENA_BOUNDS.y + ARENA_BOUNDS.height / 2;
  const { balls, ...parameterOverrides } = tuning;
  const parameters = resolveLevelParameters(parameterOverrides);
  const rotation = degToRad(parameters.rotationDeg);
  const tangentDirection = parameters.swirl === "ccw" ? -1 : 1;

  return {
    level,
    durationMs: parameters.durationMs,
    forceStrength: parameters.forceStrength,
    bounce: parameters.bounce,
    walls: {
      left: parameters.leftWallColor,
      right: parameters.rightWallColor,
      top: parameters.topWallColor,
      bottom: parameters.bottomWallColor,
    },
    balls: balls.map((ball) => {
      const angle = degToRad(ball.angleDeg) + rotation;
      const radialX = Math.cos(angle);
      const radialY = Math.sin(angle);
      const tangentX = -radialY * tangentDirection;
      const tangentY = radialX * tangentDirection;
      const speed = parameters.baseSpeed * (ball.speedScale ?? 1);
      const radialBias = ball.radialBias ?? 0;

      return {
        x: clamp(
          centerX + radialX * parameters.radiusX,
          ARENA_BOUNDS.x + BALL_RADIUS,
          ARENA_BOUNDS.x + ARENA_BOUNDS.width - BALL_RADIUS,
        ),
        y: clamp(
          centerY + radialY * parameters.radiusY,
          ARENA_BOUNDS.y + BALL_RADIUS,
          ARENA_BOUNDS.y + ARENA_BOUNDS.height - BALL_RADIUS,
        ),
        vx: tangentX * speed + radialX * radialBias,
        vy: tangentY * speed + radialY * radialBias,
        charge: ball.charge,
      };
    }),
  };
}

function resolveLevelParameters(
  overrides: Partial<AuthoredLevelParameters>,
): AuthoredLevelParameters {
  return {
    ...DEFAULT_LEVEL_PARAMETERS,
    ...overrides,
  };
}

function createGeneratedLevel(level: LevelId): LevelDefinition {
  const rng = new DeterministicRandom(seedForLevel(level));
  const generatedOffset = getPrimaryLevelNumber(level) - FIRST_GENERATED_LEVEL_MAJOR + 1;
  const ballCount = FINAL_AUTHORED_BALL_COUNT + Math.ceil(generatedOffset / 2);
  const centerX = ARENA_BOUNDS.x + ARENA_BOUNDS.width / 2;
  const centerY = ARENA_BOUNDS.y + ARENA_BOUNDS.height / 2;
  const charges = buildChargePattern(ballCount);
  const ringRadius =
    Math.min(ARENA_BOUNDS.width, ARENA_BOUNDS.height) * 0.28 + generatedOffset * 10;
  const maxRadius =
    Math.min(ARENA_BOUNDS.width, ARENA_BOUNDS.height) / 2 - BALL_RADIUS - 18;

  return {
    level,
    durationMs: DEFAULT_LEVEL_PARAMETERS.durationMs,
    forceStrength: DEFAULT_LEVEL_PARAMETERS.forceStrength,
    bounce: DEFAULT_LEVEL_PARAMETERS.bounce,
    walls: {
      left: DEFAULT_LEVEL_PARAMETERS.leftWallColor,
      right: DEFAULT_LEVEL_PARAMETERS.rightWallColor,
      top: DEFAULT_LEVEL_PARAMETERS.topWallColor,
      bottom: DEFAULT_LEVEL_PARAMETERS.bottomWallColor,
    },
    balls: charges.map((charge, index) => {
      const angle =
        -Math.PI / 2 +
        (Math.PI * 2 * index) / ballCount +
        (rng.next() - 0.5) * 0.22;
      const radius = Math.min(ringRadius * (0.9 + rng.next() * 0.12), maxRadius);
      const radialX = Math.cos(angle);
      const radialY = Math.sin(angle);
      const tangentX = -radialY;
      const tangentY = radialX;
      const swirl = index % 2 === 0 ? 1 : -1;
      const speed = 34 + generatedOffset * 3 + rng.next() * 10;
      const radialNudge = (rng.next() - 0.5) * 10;

      return {
        x: clamp(
          centerX + radialX * radius,
          ARENA_BOUNDS.x + BALL_RADIUS,
          ARENA_BOUNDS.x + ARENA_BOUNDS.width - BALL_RADIUS,
        ),
        y: clamp(
          centerY + radialY * radius,
          ARENA_BOUNDS.y + BALL_RADIUS,
          ARENA_BOUNDS.y + ARENA_BOUNDS.height - BALL_RADIUS,
        ),
        vx: tangentX * speed * swirl + radialX * radialNudge,
        vy: tangentY * speed * swirl + radialY * radialNudge,
        charge,
      };
    }),
  };
}

function buildAuthoredLevels(
  specs: AuthoredLevelSpecMap,
): Record<LevelId, AuthoredLevelTuning> {
  const levels: Record<LevelId, AuthoredLevelTuning> = {};

  for (const levelKey of Object.keys(specs) as AuthoredLevelKey[]) {
    levels[levelKeyToId(levelKey)] = createProgressionTuning(specs[levelKey]);
  }

  return levels;
}

function createProgressionTuning(spec: ProgressionLevelSpec): AuthoredLevelTuning {
  const {
    ballCount,
    startCharge = -1,
    angleOffsetDeg = -90,
    speedScalePattern = [1],
    radialBiasPattern = [0],
    angleJitterPattern = [0],
    ...parameterOverrides
  } = spec;

  return {
    ...parameterOverrides,
    balls: buildOrbitBalls(
      ballCount,
      startCharge,
      angleOffsetDeg,
      speedScalePattern,
      radialBiasPattern,
      angleJitterPattern,
    ),
  };
}

function buildOrbitBalls(
  ballCount: number,
  startCharge: Charge,
  angleOffsetDeg: number,
  speedScalePattern: readonly number[],
  radialBiasPattern: readonly number[],
  angleJitterPattern: readonly number[],
): OrbitBallTuning[] {
  return Array.from({ length: ballCount }, (_, index) => {
    const charge = index % 2 === 0 ? startCharge : invertCharge(startCharge);
    const angleStep = (360 * index) / ballCount;
    const angleJitter = angleJitterPattern[index % angleJitterPattern.length] ?? 0;
    const speedScale = speedScalePattern[index % speedScalePattern.length] ?? 1;
    const radialBias = radialBiasPattern[index % radialBiasPattern.length] ?? 0;

    return {
      charge,
      angleDeg: normalizeAngle(angleOffsetDeg + angleStep + angleJitter),
      speedScale: speedScale === 1 ? undefined : speedScale,
      radialBias: radialBias === 0 ? undefined : radialBias,
    };
  });
}

function buildChargePattern(ballCount: number): Charge[] {
  let negatives = ballCount - Math.floor(ballCount / 2);
  let positives = ballCount - negatives;
  let nextCharge: Charge = -1;
  const charges: Charge[] = [];

  while (charges.length < ballCount) {
    if (nextCharge === -1 && negatives > 0) {
      charges.push(-1);
      negatives -= 1;
    } else if (nextCharge === 1 && positives > 0) {
      charges.push(1);
      positives -= 1;
    } else if (negatives > 0) {
      charges.push(-1);
      negatives -= 1;
    } else {
      charges.push(1);
      positives -= 1;
    }

    nextCharge = nextCharge === -1 ? 1 : -1;
  }

  return charges;
}

function levelKeyToId(levelKey: AuthoredLevelKey): LevelId {
  return levelKey.slice("level_".length).replaceAll("_", ".");
}

function levelIdToKey(levelId: LevelId): AuthoredLevelKey {
  return `level_${levelId.replaceAll(".", "_")}`;
}

function parseLevelIdSegments(levelId: LevelId): number[] {
  return levelId.split(".").map((segment) => Number.parseInt(segment, 10));
}

function compareLevelIds(left: LevelId, right: LevelId): number {
  const leftSegments = parseLevelIdSegments(left);
  const rightSegments = parseLevelIdSegments(right);
  const segmentCount = Math.min(leftSegments.length, rightSegments.length);

  for (let index = 0; index < segmentCount; index += 1) {
    const difference = leftSegments[index] - rightSegments[index];
    if (difference !== 0) {
      return difference;
    }
  }

  return leftSegments.length - rightSegments.length;
}

function getPrimaryLevelNumber(levelId: LevelId): number {
  return parseLevelIdSegments(levelId)[0];
}

function isIntegerLevelId(levelId: LevelId): boolean {
  return !levelId.includes(".");
}

function isGeneratedLevelId(levelId: LevelId): boolean {
  return isIntegerLevelId(levelId) && getPrimaryLevelNumber(levelId) >= FIRST_GENERATED_LEVEL_MAJOR;
}

function degToRad(value: number): number {
  return (value * Math.PI) / 180;
}

function invertCharge(charge: Charge): Charge {
  return charge === 1 ? -1 : 1;
}

function normalizeAngle(value: number): number {
  const normalized = ((value % 360) + 360) % 360;
  return normalized > 180 ? normalized - 360 : normalized;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
